/**
 * Centralised social-handle constants + share helpers.
 *
 * Single source of truth for every place in the app that links to
 * CampCompass UK's social profiles or asks the user to tag us.
 *
 * Configure via /app/frontend/.env (REACT_APP_FACEBOOK_URL etc.) so
 * handles can change without touching code.
 */

/**
 * Append a cache-buster query string to a URL so social-platform scrapers
 * (Facebook, LinkedIn, Slack, WhatsApp) treat it as a fresh URL and skip
 * their 24-72h OG cache. Also handy when you've just updated your og:image
 * and want the new preview to show immediately.
 *
 * Strategy: a per-day token (`v={YYYYMMDD}`). Same token within a day so
 * shares cluster and earn cumulative reach, fresh token after midnight so
 * any OG changes propagate the next morning automatically.
 *
 * Why per-day not per-millisecond: a brand-new ?v= every keystroke fragments
 * Facebook's link-preview cache and stops reaching peoples' walls. Per-day
 * is the sweet spot.
 */
export function withShareCacheBust(url) {
  if (!url) return url;
  const today = new Date();
  const tag = today.getUTCFullYear().toString() +
              String(today.getUTCMonth() + 1).padStart(2, "0") +
              String(today.getUTCDate()).padStart(2, "0");
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${tag}`;
}

export const SOCIAL = {
  facebook: {
    url: process.env.REACT_APP_FACEBOOK_URL || "https://www.facebook.com/campcompassuk2026",
    handle: process.env.REACT_APP_FACEBOOK_HANDLE || "@campcompassuk2026",
    label: "Facebook",
  },
  instagram: {
    url: process.env.REACT_APP_INSTAGRAM_URL || "https://www.instagram.com/camp.compassuk2026",
    handle: process.env.REACT_APP_INSTAGRAM_HANDLE || "@camp.compassuk2026",
    label: "Instagram",
  },
  hashtag: process.env.REACT_APP_SOCIAL_HASHTAG || "#CampCompassUK",
};

/**
 * Produce a pre-filled "share" caption that already mentions CampCompass UK
 * and the brand hashtag, so any post a camper makes promotes us automatically.
 *
 * Usage:
 *   buildShareCaption({ kind: "campsite", name: "Great Langdale", region: "Lake District" })
 *   buildShareCaption({ kind: "review",   name: "Great Langdale", rating: 5 })
 *   buildShareCaption({ kind: "trip",     name: "Great Langdale", date: "5-7 May" })
 */
export function buildShareCaption({ kind, name, region, rating, date, custom }) {
  if (custom) return custom;
  const tag = `${SOCIAL.instagram.handle} ${SOCIAL.hashtag}`;
  if (kind === "review" && rating) {
    return `Just gave ${name} ${rating}★ on CampCompass UK 🏕️\n\nThe honest UK campsite app — find a real patch via ${tag}`;
  }
  if (kind === "trip") {
    return `Pitch booked! ${name}${date ? ` · ${date}` : ""} 🏕️\n\nFound via the only honest UK campsite app — ${tag}`;
  }
  // default: campsite recommendation
  return `Heading to ${name}${region ? ` in ${region}` : ""} — found on CampCompass UK 🧭\n\nThe honest UK campsite app, every site in one place. ${tag}`;
}

/**
 * Open a Facebook share URL with pre-filled text.
 * Falls back to copying caption to clipboard so users can paste manually
 * (Facebook's mobile sharer ignores `quote=`).
 */
export function shareToFacebook({ url, caption }) {
  const shareUrl = "https://www.facebook.com/sharer/sharer.php?u=" +
    encodeURIComponent(url || window.location.href) +
    "&quote=" + encodeURIComponent(caption || "");
  window.open(shareUrl, "_blank", "noopener,width=600,height=520");
}

/**
 * Instagram has no web share API — best UX is: copy the caption to
 * clipboard and direct the user to the Instagram app/web. Caller should
 * show a toast confirming the copy.
 */
export async function shareToInstagram({ caption }) {
  try { await navigator.clipboard.writeText(caption); } catch { /* clipboard not available */ }
  // The username deep link works on mobile + desktop
  window.open(SOCIAL.instagram.url, "_blank", "noopener");
  return true;
}
